import { CboRole, FeatureFlag, Package } from '@cbo/shared-library';
import i18next from 'i18next';
import { ReactNode } from 'react';
import routes from '../../../constants/routes';
import { usePackage } from '../../../contexts/packageContext';
import { useUsers } from '../../../contexts/userContext';
import isPermitted from '../../../lib/permissions';
import { useFeatureFlags } from '../../../utils/hooks/useFeatureFlag';
import useCompanyLinksRoutes from './useCompanyLinksRoutes';
import useInventoryRoutes from './useInventoryRoutes';
import useLaborRoutes from './useLaborRoutes';
import useSalesRoutes from './useSalesRoutes';
import useSettingsRoutes from './useSettingsRoutes';

export type NavItem = {
  title?: string;
  children?: NavItem[];
  href?: string;
  icon?: ReactNode;
  key?: string;
  forceNewFrame?: boolean;
  testid?: string;
  backHref?: string;
};

export default function useNavItems(companyLinks: NavItem[]): NavItem[] {
  const user = useUsers();
  const [cboHideAlohaEsentialOrgs, cboCentralizedReports] = useFeatureFlags(
    FeatureFlag.hideAlohaEssentialOrgs,
    FeatureFlag.centralizedReports
  );
  const hasStarterPackage = usePackage(Package.STARTER);

  const laborRoutes = useLaborRoutes();
  const inventoryRoutes = useInventoryRoutes();
  const salesRoutes = useSalesRoutes();
  const companyLinksRoutes = useCompanyLinksRoutes(companyLinks);
  const settingsRoutes = useSettingsRoutes();

  const navItems: NavItem[] = [];

  if (isPermitted(user, [CboRole.ADMIN_DASHBOARD_VIEWER])) {
    navItems.push({
      title: i18next.t('navItems.dashboard'),
      testid: 'home-dashboard',
      href: routes.HOME_PAGE,
    });

    if (!cboHideAlohaEsentialOrgs) {
      navItems.push({
        title: i18next.t('navItems.laborManagement'),
        forceNewFrame: true,
        testid: 'labor-management',
        href: '/labor',
        children: laborRoutes,
      });
    }

    if (hasStarterPackage) {
      navItems.push({
        title: i18next.t('navItems.inventoryManagement'),
        href: '/inventory',
        forceNewFrame: true,
        testid: 'inventory-management',
        children: inventoryRoutes,
      });
    }

    navItems.push({
      title: i18next.t('navItems.salesManagement'),
      forceNewFrame: true,
      href: '/sales',
      testid: 'sales-management',
      children: salesRoutes,
    });

    if (hasStarterPackage) {
      navItems.push({
        title: i18next.t('navItems.companyLinks'),
        forceNewFrame: true,
        href: routes.EDIT_COMPANY_LINKS,
        testid: 'company-links-side-menu-item',
        children: companyLinksRoutes,
      });
    }

    if (cboCentralizedReports) {
      navItems.push({
        title: i18next.t('navItems.reports'),
        href: routes.ALL_REPORTS,
        testid: 'reports',
      });
    }

    if (isPermitted(user, [CboRole.AUDIT_LOG_VIEW])) {
      navItems.push({
        title: i18next.t('navItems.activityLog'),
        testid: 'activity-log-side-menu-item',
        href: routes.ACTIVITY_LOG,
      });
    }
  }

  if (isPermitted(user, [CboRole.SCHEDULE_PERSONAL_VIEW])) {
    navItems.push({
      title: i18next.t('navItems.mySchedule'),
      href: routes.MY_SCHEDULE,
      testid: 'my-schedule-menu-item',
    });
  }

  if (isPermitted(user, [CboRole.ADMIN_DASHBOARD_VIEWER])) {
    navItems.push({
      title: i18next.t('navItems.settings'),
      forceNewFrame: true,
      href: '/settings',
      testid: 'settings-side-menu-item',
      children: settingsRoutes,
    });
  }

  return navItems;
}
